'use client';

import { Content, Header, Item, Root, Trigger } from '@radix-ui/react-accordion';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Text } from '@videoblocks/design-system';
import { ChevronDown, ChevronUp } from '@videoblocks/react-icons';

export interface StockItemField {
    label: string;
    value: string | number | undefined;
}

export interface StockItemInfoDetailsProps {
    className?: string;
    defaultOpen: boolean;
    fields: StockItemField[];
    title?: string;
    variant: 'audio' | 'image' | 'video';
}

const StockItemInfoDetails = ({
    className = '',
    fields,
    title,
    variant = 'audio',
    defaultOpen = false,
    ...props
}: StockItemInfoDetailsProps) => {
    const [value, setValue] = React.useState(defaultOpen ? 'item-1' : '');

    const handleValueChange = (newValue: string) => {
        setValue(newValue);
    };

    return (
        <div className={twMerge('w-full', className)} {...props}>
            {title && variant === 'audio' && (
                <div className="mb-1">
                    <Text fontSize="lg" fontWeight="semibold" className="text-lg">
                        {title}
                    </Text>
                </div>
            )}
            <Root type="single" collapsible value={value} onValueChange={handleValueChange} className="w-full">
                <Item value="item-1" className="w-full">
                    {variant === 'audio' && (
                        <Header className="flex justify-start md:justify-end">
                            <Trigger className="flex items-center gap-2 text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 py-2 px-1 cursor-pointer">
                                <Text fontSize="xs" fontWeight="semibold" className="uppercase tracking-wider text-xs">
                                    {value === 'item-1' ? 'FEWER DETAILS' : 'MORE DETAILS'}
                                </Text>
                                {value === 'item-1' ? (
                                    <ChevronUp className="w-2 h-2 fill-gray-700" aria-hidden="true" />
                                ) : (
                                    <ChevronDown className="w-3 h-2 fill-gray-700" aria-hidden="true" />
                                )}
                            </Trigger>
                        </Header>
                    )}
                    <Content
                        forceMount
                        className="overflow-hidden data-[state=closed]:h-0 data-[state=open]:h-auto transition-[height] duration-300 ease-out"
                    >
                        <div className={twMerge('w-full', variant === 'audio' ? 'pt-2 pb-2' : '')}>
                            <ul
                                className={twMerge(
                                    'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-3 lg:grid-flow-col',
                                    variant === 'audio' ? 'gap-x-15 gap-y-2' : '',
                                )}
                            >
                                {fields.map((field, index) => (
                                    <li
                                        key={`${field.label}-${index}`}
                                        className={variant === 'audio' ? 'md:min-w-65 lg:min-w-80' : ''}
                                    >
                                        <Text
                                            fontSize="sm"
                                            className={twMerge(
                                                'font-normal text-sm',
                                                variant === 'audio' ? 'text-gray-900' : '',
                                            )}
                                        >
                                            {field.label}: <span className="font-semibold">{field.value}</span>
                                        </Text>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Content>
                </Item>
            </Root>
        </div>
    );
};

export default StockItemInfoDetails;
